import { FC, PropsWithChildren, useCallback, useContext, useMemo } from "react";
import { FavoriteEventsContext, FeedContext, useSportsbookConfig } from "@finbackoffice/site-core";
import classNames from "classnames";
import { Settings } from "react-slick";
import { ISportFeedResponse } from "@finbackoffice/websocket-client";
import { SportsbookLayoutEnum } from "@finbackoffice/fe-core";
import SlickSlider from "components/base/slick-slider/SlickSlider";
import Translate from "components/base/translate/Translate";
import { FeedTypeEnum, useGetCurrentFeedType } from "hooks";
import FadeInAnimation from "components/base/fade-in/FadeInAnimation";
import styles from "./sports-layout.module.sass";
import Sport from "./sport/Sport";

interface IProps {
    sports: ISportFeedResponse[];
    activeSportId?: number;
    onSportClick?: (sportId: number) => void;
    showGamesCount?: boolean;
    variant?: "default" | "widget";
    sliderSettings?: Settings;
}

const SportsLayout: FC<PropsWithChildren<IProps>> = ({
    sports,
    children,
    activeSportId,
    onSportClick,
    showGamesCount = true,
    variant = "default",
    sliderSettings,
}) => {
    const feed = useGetCurrentFeedType();
    const { liveTopLeagues } = useContext(FeedContext);
    const { favEvents, favPrematchLeagues } = useContext(FavoriteEventsContext);
    const sportsbookConfig = useSportsbookConfig();
    const isFlatLayout = sportsbookConfig.type === SportsbookLayoutEnum.Flat;

    const defaultSettings: Settings = useMemo(
        () => ({
            dots: false,
            infinite: false,
            variableWidth: true,
            speed: 1000,
            slidesToShow: isFlatLayout ? 5 : 7,
            slidesToScroll: 1,
            arrows: sports.length > 3,
            responsive: [
                {
                    breakpoint: 1800,
                    settings: {
                        slidesToShow: 6,
                    },
                },
                {
                    breakpoint: 1696,
                    settings: {
                        slidesToShow: 5,
                    },
                },
                {
                    breakpoint: 1530,
                    settings: {
                        slidesToShow: 4,
                    },
                },
                {
                    breakpoint: 1000,
                    settings: {
                        slidesToShow: 3,
                    },
                },
            ],
            ...sliderSettings,
        }),
        [sports.length, sliderSettings, isFlatLayout],
    );

    const hasFavorite = useCallback(
        (id: number) => {
            if (feed === FeedTypeEnum.live) {
                return !!favEvents.find((event) => event.sport_id === id) || false;
            }

            if (feed === FeedTypeEnum.prematch) {
                return !!favPrematchLeagues.find((event) => event.sport_id === id) || false;
            }

            return false;
        },
        [favEvents, favPrematchLeagues, feed],
    );

    return (
        <>
            {!!sports.length ? (
                <FadeInAnimation>
                    <div className={styles.container}>
                        <SlickSlider settings={defaultSettings}>
                            {sports.map((sport) => (
                                <Sport
                                    variant={variant === "widget" ? "button" : "link"}
                                    key={sport.id}
                                    sport={sport}
                                    active={sport.id === activeSportId}
                                    hasFavorite={
                                        hasFavorite(sport.id) ||
                                        !!liveTopLeagues.find(
                                            (tournament) => tournament.sport_id === sport.id,
                                        )
                                    }
                                    onClickHandler={onSportClick}
                                    showGamesCount={showGamesCount}
                                />
                            ))}
                        </SlickSlider>
                    </div>
                </FadeInAnimation>
            ) : (
                <div className={classNames("no-game", styles.noGame)}>
                    <Translate tid="no_games" />
                </div>
            )}
            {children}
        </>
    );
};

export default SportsLayout;
